// src/scripts/exitSignalsNative.ts
/*
 * Exit signals for the popup engine: a top-ward mouse leave plus four signals
 * that need no cursor (tab hidden, window blur, fast scroll to top, the
 * Cmd+L / Ctrl+L address bar shortcut), behind an arming guard. `armExit` in
 * popupTriggers.ts delegates here.
 *
 * We wrote these instead of vendoring a library. Research §9 built both options
 * and measured them: the library costs 1.26 KB gzipped for four signals we then
 * have to translate, this costs 0.33 KB. The behaviours are the libraries', with
 * each one traced to its source in that section and in the reference index of
 * docs/popup-triggers-dashboard_plan.md.
 *
 * The returned teardown has two callers. The engine runs it when a trigger
 * fires or a host disarms, and it runs it again when a visitor withdraws
 * tracking consent mid-session (`revokeTracking` in popupTriggers.ts), which is
 * the CCPA case CLAUDE.md §13 describes: an opt-out has to stop the observing,
 * not only the counting, in the session it was made in.
 *
 * Deliberately omits the library's `timeOnPage` and `pageViews` triggers: the
 * first is a delay trigger masquerading as a guard, and we already ship `delay`;
 * the second belongs in frequency capping, not exit detection.
 */

/** `L`, as the deprecated `KeyboardEvent.keyCode` still reports it. */
const L_KEY_CODE = 76;

/**
 * How long with no interaction before active time stops accumulating, in ms.
 * Below this, a visitor who has not moved in a moment still counts as present
 * (reading does not mean touching nothing every second); past it, a tab left
 * open with nobody there stops counting towards `minTimeOnPageMs` or `delay`,
 * the same way a hidden tab already does. Research
 * docs/popups-exit-intent_research.md §14.2 item 1 names the idea ("count only
 * while the tab is visible and the visitor has produced input recently") and
 * not a number; 3 seconds is this plan's own judgement call (task 2.1).
 */
export const ACTIVE_TIME_IDLE_MS = 3000;

/**
 * Interaction events that count as "recently active" for `createActiveTimeClock`.
 * Deliberately the same breadth as `INACTIVITY_EVENTS` in popupTriggers.ts
 * (mouse, keyboard, scroll, touch), kept as its own list rather than imported:
 * that module imports from this one, so the reverse would be a cycle, and this
 * module stays free of any import that is not the DOM itself.
 */
const ACTIVE_TIME_EVENTS = [
  "mousemove",
  "mousedown",
  "click",
  "keydown",
  "scroll",
  "touchstart",
  "pointerdown",
  "wheel",
] as const;

/**
 * Milliseconds of visible, recently-active page time since creation: paused
 * while `document.visibilityState === "hidden"` and while no `ACTIVE_TIME_EVENTS`
 * interaction has happened in the last `ACTIVE_TIME_IDLE_MS`, resumed the moment
 * either condition clears. A host with no readable `document` (a non-DOM
 * embed) reports plain wall-clock time instead of throwing: nothing to pause on
 * means nothing is ever paused.
 *
 * `whenElapsed` schedules a callback for the moment accumulated active time
 * reaches a target, re-armed around every pause/resume rather than a single
 * `setTimeout` against wall-clock time, which is what let a tab hidden for a
 * minute satisfy a 15-second guard it never actually waited out.
 */
export interface ActiveTimeClock {
  /** Active milliseconds accumulated so far. */
  elapsedMs(): number;
  /**
   * Call `cb` once `elapsedMs()` reaches `targetMs` (immediately if already
   * there, or if `targetMs <= 0`). Returns a teardown that cancels the wait.
   */
  whenElapsed(targetMs: number, cb: () => void): () => void;
  /** Stop every listener and pending wait. Idempotent. */
  stop(): void;
}

export function createActiveTimeClock(): ActiveTimeClock {
  const hasDocument = typeof document !== "undefined";
  let accumulated = 0;
  let segmentStartAt: number | null = null;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  const waiters = new Set<{
    targetMs: number;
    cb: () => void;
    timer: ReturnType<typeof setTimeout> | null;
  }>();

  const isHidden = (): boolean =>
    hasDocument && document.visibilityState === "hidden";

  /** Fold the running segment into `accumulated` without ending it. */
  function checkpoint(): void {
    if (segmentStartAt === null) return;
    const now = Date.now();
    accumulated += now - segmentStartAt;
    segmentStartAt = now;
  }

  function rescheduleWaiters(): void {
    const elapsed = elapsedMs();
    for (const waiter of Array.from(waiters)) {
      if (waiter.timer) {
        clearTimeout(waiter.timer);
        waiter.timer = null;
      }
      if (elapsed >= waiter.targetMs) {
        waiters.delete(waiter);
        waiter.cb();
        continue;
      }
      if (segmentStartAt !== null) {
        const remaining = waiter.targetMs - elapsed;
        waiter.timer = setTimeout(rescheduleWaiters, remaining);
      }
      // Paused (hidden or idle): leave the wait unscheduled. The next resume
      // reschedules it against the then-current remaining time.
    }
  }

  function armIdleTimer(): void {
    if (idleTimer) clearTimeout(idleTimer);
    if (!hasDocument) return; // nothing to go idle against
    idleTimer = setTimeout(pause, ACTIVE_TIME_IDLE_MS);
  }

  function pause(): void {
    checkpoint();
    segmentStartAt = null;
    if (idleTimer) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
    rescheduleWaiters();
  }

  function resume(): void {
    if (segmentStartAt !== null) return; // already running
    segmentStartAt = Date.now();
    armIdleTimer();
    rescheduleWaiters();
  }

  function onVisibilityChange(): void {
    if (isHidden()) pause();
    else resume();
  }

  function onActivity(): void {
    if (isHidden()) return; // cannot be active on a tab nobody can see
    if (segmentStartAt === null) resume();
    else armIdleTimer(); // extend the grace window
  }

  if (!hasDocument || !isHidden()) resume();
  if (hasDocument) {
    document.addEventListener("visibilitychange", onVisibilityChange);
    for (const name of ACTIVE_TIME_EVENTS) {
      document.addEventListener(name, onActivity, {
        passive: true,
        capture: true,
      });
    }
  }

  function elapsedMs(): number {
    checkpoint();
    return accumulated;
  }

  function whenElapsed(targetMs: number, cb: () => void): () => void {
    if (targetMs <= 0) {
      cb();
      return () => {};
    }
    const waiter = {
      targetMs,
      cb,
      timer: null as ReturnType<typeof setTimeout> | null,
    };
    waiters.add(waiter);
    rescheduleWaiters();
    return () => {
      if (waiter.timer) clearTimeout(waiter.timer);
      waiters.delete(waiter);
    };
  }

  function stop(): void {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = null;
    for (const waiter of waiters) {
      if (waiter.timer) clearTimeout(waiter.timer);
    }
    waiters.clear();
    if (hasDocument) {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      for (const name of ACTIVE_TIME_EVENTS) {
        document.removeEventListener(name, onActivity, { capture: true });
      }
    }
  }

  return { elapsedMs, whenElapsed, stop };
}

/**
 * How far down the viewport a departing cursor still counts as leaving through
 * the top, in px. ouibounce's `sensitivity`, same default.
 *
 * `mouseout` reports where the pointer was when it crossed the boundary, and a
 * cursor thrown at the tab strip is moving fast enough to be sampled a few
 * pixels inside the window, so the top pixel alone misses the gesture.
 */
export const DEFAULT_EXIT_SENSITIVITY_PX = 20;

/**
 * How long the module waits after the cursor leaves before believing it, in ms.
 * exit-intent-js's `mouseLeaveDelay`, same default. Cancelled if the cursor
 * comes back, so a cursor crossing the top edge on its way somewhere else does
 * not open the popup.
 */
export const DEFAULT_MOUSE_LEAVE_DELAY_MS = 1000;

/**
 * Nothing counts before the visitor has been on the page this long, in ms.
 * ouibounce's `timer` in shape: it guards the signals rather than firing them,
 * so when it elapses nothing opens and the gesture still has to arrive.
 *
 * The engine re-exports this as `MIN_TIME_ON_PAGE_MS` and hands it back down
 * per form, so the number is written once. It was written twice, here and
 * there, and the two agreeing was load-bearing for every caller that arms this
 * module directly.
 */
export const DEFAULT_MIN_TIME_ON_PAGE_MS = 15000;

/**
 * Upward travel that counts as leaving, in px, by device class.
 * exit-intent-js's `scrollUpThreshold`, same defaults. The phone number is the
 * lower one because the gesture is the same flick either way, and it covers
 * more of a phone's screen than of a monitor.
 */
export const DEFAULT_SCROLL_UP_THRESHOLD_PX = {
  mobile: 200,
  desktop: 400,
} as const;

/** Viewport width at or below which the mobile threshold applies, in px. */
export const DEFAULT_MOBILE_BREAKPOINT_PX = 768;

/**
 * How long the upward travel has to happen inside, in ms. This is what makes
 * the threshold a speed rather than a distance: 400px covered in half a second
 * is someone reaching for the URL bar, and the same 400px covered over five
 * seconds is someone reading their way back up the page.
 */
export const DEFAULT_SCROLL_UP_WINDOW_MS = 500;

/**
 * How close to the top of the page the flick has to end, in px. The gesture is
 * a reach for the browser chrome, which sits above a page that has been
 * scrolled to its start; a fast scroll up that stops mid-article is navigation.
 */
export const SCROLL_NEAR_TOP_PX = 100;

export interface NativeExitOptions {
  /** Nothing opens before the visitor has been here this long. */
  minTimeOnPageMs?: number;
  /**
   * Top-edge band a departing cursor has to be inside for the departure to
   * count (default 20). 0 = the top edge exactly, the pre-band behaviour.
   */
  exitSensitivityPx?: number;
  /** Grace period after the cursor leaves before it counts as leaving. */
  mouseLeaveDelayMs?: number;
  /**
   * Upward travel that counts as leaving, by device class (default 200 mobile
   * / 400 desktop). Read together with `scrollUpWindowMs`: it is a distance per
   * window, so it describes a speed. 0 = any upward move near the top counts.
   */
  scrollUpThreshold?: { mobile: number; desktop: number };
  /** Viewport width at or below which the mobile threshold applies. */
  mobileBreakpoint?: number;
  /**
   * Window the upward travel has to happen inside, in ms (default 500). 0 turns
   * the flick signal off, since no run can complete inside a window of no
   * length; the other four signals are unaffected.
   */
  scrollUpWindowMs?: number;
  /**
   * Which of the five signals to bind (task 2.1). Default: all five
   * (`TOUCH_EXIT_SIGNALS`), so a direct caller that passes no `signals` option
   * is unchanged. `popupTriggers.ts`'s `armExit` is the one caller that
   * narrows this, per device.
   */
  signals?: readonly NativeExitReason[];
}

/**
 * Coerce a px/ms option: finite and >= 0, else the default. 0 is a meaningful
 * value for all three (no band, no grace, no guard), so it is kept rather than
 * treated as absent. Repair lives here rather than in the engine because the
 * engine forwards these verbatim: one implementation, so a value arriving
 * straight from a host is repaired the same way as one from a form's settings.
 */
function toNonNegative(value: number | undefined, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return fallback;
  }
  return value;
}

/**
 * Which of the five cursor-free-or-not departure signals actually fired (task
 * 4.1, docs/popup-triggers-and-funnel_plan.md): `mouse_top` (cursor through the
 * top edge), `blur` (window lost focus), `visibility` (tab hidden), `flick`
 * (fast upward scroll near the top) or `keyboard` (Cmd+L/Ctrl+L). Every signal
 * used to report the same literal `"exit"`, so an owner could not tell a
 * popup opened by a window losing focus from one opened by a genuine
 * cursor-leaves-the-page gesture, even though the two have very different
 * false-positive rates. `popupTriggers.ts`'s `armExit` passes this straight
 * through to `onFire`, which is what the embed puts on the `popup_shown`
 * beacon's `reason` field.
 */
export type NativeExitReason =
  | "mouse_top"
  | "blur"
  | "visibility"
  | "flick"
  | "keyboard";

/**
 * The two signals a mouse-using visitor can only produce by actually leaving:
 * the cursor crossing the top edge, or a fast scroll back to the top. The
 * other three (`blur`, `visibility`, `keyboard`) happen routinely while a
 * desktop visitor is still reading (switching windows to check something,
 * hiding the tab, pressing Cmd+L for an unrelated reason), so counting them as
 * an exit on a computer is what opened the popup for Ben on 2026-10-29 before
 * he had shown any interest (task 2.1, docs/popup-exit-signals-fix_plan.md).
 */
export const COMPUTER_EXIT_SIGNALS: readonly NativeExitReason[] = [
  "mouse_top",
  "flick",
];

/**
 * All five signals: the default for a direct caller that passes no `signals`
 * option, and the set a touch device keeps, since none of the four
 * cursor-free signals is ambiguous there (there is no mouse to be doing
 * something else with).
 */
export const TOUCH_EXIT_SIGNALS: readonly NativeExitReason[] = [
  "mouse_top",
  "blur",
  "visibility",
  "flick",
  "keyboard",
];

export function armNativeExitSignals(
  hit: (reason: NativeExitReason) => boolean,
  options: NativeExitOptions = {},
): () => void {
  if (typeof window === "undefined") return () => {};

  const armedAt = Date.now();
  // Gates `fire()` below on visible, recently-active time rather than
  // wall-clock since arming (task 2.1, docs/popup-triggers-and-funnel_plan.md):
  // a tab hidden for a minute no longer satisfies a 15-second guard it never
  // actually waited out.
  const activeClock = createActiveTimeClock();
  const minTime = toNonNegative(
    options.minTimeOnPageMs,
    DEFAULT_MIN_TIME_ON_PAGE_MS,
  );
  const sensitivity = toNonNegative(
    options.exitSensitivityPx,
    DEFAULT_EXIT_SENSITIVITY_PX,
  );
  const mouseLeaveDelay = toNonNegative(
    options.mouseLeaveDelayMs,
    DEFAULT_MOUSE_LEAVE_DELAY_MS,
  );
  const thresholds = {
    mobile: toNonNegative(
      options.scrollUpThreshold?.mobile,
      DEFAULT_SCROLL_UP_THRESHOLD_PX.mobile,
    ),
    desktop: toNonNegative(
      options.scrollUpThreshold?.desktop,
      DEFAULT_SCROLL_UP_THRESHOLD_PX.desktop,
    ),
  };
  const breakpoint = toNonNegative(
    options.mobileBreakpoint,
    DEFAULT_MOBILE_BREAKPOINT_PX,
  );
  const scrollWindow = toNonNegative(
    options.scrollUpWindowMs,
    DEFAULT_SCROLL_UP_WINDOW_MS,
  );
  const activeSignals = new Set<NativeExitReason>(
    options.signals ?? TOUCH_EXIT_SIGNALS,
  );

  let done = false;
  let mouseTimer: ReturnType<typeof setTimeout> | null = null;
  // The previous scroll sample, and the start of the upward run in progress.
  let lastScrollY = window.scrollY;
  let runStartY = lastScrollY;
  let runStartAt = armedAt;

  const fire = (reason: NativeExitReason) => {
    if (done) return;
    if (activeClock.elapsedMs() < minTime) return;
    done = true;
    hit(reason);
  };

  /**
   * The top-edge band in effect right now, capped at the window height so a
   * band taller than the viewport cannot turn "left through the top" into "left
   * through any edge", which is the distinction the trigger rests on. Read per
   * event rather than at arming time, so a resize or an orientation change is
   * accounted for.
   */
  const topEdgeBand = (): number => {
    const viewport = window.innerHeight;
    return viewport > 0 ? Math.min(sensitivity, viewport) : sensitivity;
  };

  // 1. Cursor leaves through the top of the viewport. A departure through any
  // other edge is not exit intent (the next tab, the dock and the URL bar are
  // all up), so the top band is checked as well as the window boundary.
  const onMouseOut = (e: MouseEvent) => {
    if (e.relatedTarget) return;
    if (e.clientY > topEdgeBand()) return;
    // One handle, so a second departure replaces the first rather than leaving
    // it running: `mouseover` clears whatever `mouseTimer` points at, and an
    // orphaned timer opens the popup on a visitor who came back. Two departures
    // with no re-entry between them is what moving into an iframe produces, and
    // the reference (ouibounce `_delayTimer`) holds one handle for the same
    // reason.
    if (mouseTimer) clearTimeout(mouseTimer);
    mouseTimer = setTimeout(() => fire("mouse_top"), mouseLeaveDelay);
  };
  const onMouseOver = () => {
    if (mouseTimer) clearTimeout(mouseTimer);
    mouseTimer = null;
  };

  // 2. Tab hidden. Works without a cursor, which is the whole point on mobile.
  const onVisibility = () => {
    if (document.visibilityState === "hidden") fire("visibility");
  };

  // 3. Window lost focus (switched app, clicked the address bar).
  const onBlur = () => fire("blur");

  // 4. Fast upward scroll ending near the top: the mobile "reach for the URL
  // bar" move, and the only departure gesture a visitor makes without a cursor
  // and without leaving the page.
  //
  // Speed, not distance between two events. A real flick arrives as a stream of
  // small deltas, about one per frame, so comparing consecutive samples against
  // a 400px threshold detected nothing a thumb could do; the only thing that
  // cleared it was a single jump, which is a "back to top" button or a restored
  // scroll position rather than someone leaving. The travel is measured across
  // a run of samples instead, and the run has to complete inside `scrollWindow`.
  const onScroll = () => {
    const y = window.scrollY;
    const now = Date.now();
    const threshold =
      window.innerWidth <= breakpoint ? thresholds.mobile : thresholds.desktop;

    // A downward move, or a run that has outlived the window, starts a new run
    // here. Starting it at the current position is what stops a single event
    // from firing on its own: after a pause the first sample opens a run with
    // no travel behind it, so a jump to the top (a "back to top" button, a
    // restored scroll position) is not read as a gesture.
    if (y >= lastScrollY || now - runStartAt > scrollWindow) {
      runStartY = y;
      runStartAt = now;
    }
    lastScrollY = y;

    // Travel is measured from the start of the run, which the reset above keeps
    // inside the window, so this is a floor on speed and not on distance.
    const travelled = runStartY - y;
    if (travelled > 0 && travelled >= threshold && y <= SCROLL_NEAR_TOP_PX) {
      fire("flick");
    }
  };

  // 5. Cmd+L (macOS) or Ctrl+L (Windows, Linux), the shortcut that focuses the
  // address bar: whatever is typed there next is somewhere other than this
  // page. The keypress lands before the focus moves, so it reaches the same
  // conclusion as `blur` a moment earlier, and it says so more definitely: a
  // blur is also a switch to another app. ouibounce reads only
  // `metaKey && keyCode === 76`, which is Mac-only; `keyCode` is deprecated, so
  // `key` is read too for the day a browser stops sending it.
  const onKeydown = (e: KeyboardEvent) => {
    if (!e.metaKey && !e.ctrlKey) return;
    const isL = e.keyCode === L_KEY_CODE || String(e.key).toLowerCase() === "l";
    if (isL) fire("keyboard");
  };

  // Each signal's listener binds only when `activeSignals` names it, so a
  // signal left out (desktop drops `blur`, `visibility`, `keyboard`) never
  // observes anything rather than observing and discarding: the device check
  // is in what gets attached, not in a condition inside `fire`.
  if (activeSignals.has("mouse_top")) {
    document.addEventListener("mouseout", onMouseOut);
    document.addEventListener("mouseover", onMouseOver);
  }
  if (activeSignals.has("visibility")) {
    document.addEventListener("visibilitychange", onVisibility);
  }
  if (activeSignals.has("keyboard")) {
    document.addEventListener("keydown", onKeydown);
  }
  if (activeSignals.has("blur")) {
    window.addEventListener("blur", onBlur);
  }
  if (activeSignals.has("flick")) {
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  return () => {
    activeClock.stop();
    if (mouseTimer) clearTimeout(mouseTimer);
    if (activeSignals.has("mouse_top")) {
      document.removeEventListener("mouseout", onMouseOut);
      document.removeEventListener("mouseover", onMouseOver);
    }
    if (activeSignals.has("visibility")) {
      document.removeEventListener("visibilitychange", onVisibility);
    }
    if (activeSignals.has("keyboard")) {
      document.removeEventListener("keydown", onKeydown);
    }
    if (activeSignals.has("blur")) {
      window.removeEventListener("blur", onBlur);
    }
    if (activeSignals.has("flick")) {
      window.removeEventListener("scroll", onScroll);
    }
  };
}
